import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  X, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Calendar, 
  Users,
  Video,
  BookOpen,
  Upload,
  Mail,
  FileText,
  Save,
  ChevronDown,
  ChevronRight,
  Megaphone,
  MessageCircle,
  CheckCircle,
  Send,
  AlertCircle,
  Image as ImageIcon,
  Award,
  Download,
  Loader2
} from 'lucide-react';
import { 
  createBatch,
  getBatches,
  updateBatch,
  deleteBatch,
  addBatchClass,
  getBatchClasses,
  updateBatchClass,
  deleteBatchClass,
  enrollStudentInBatch,
  getBatchEnrollments,
  removeBatchEnrollment,
  addBatchStudyMaterial,
  getBatchStudyMaterials,
  deleteBatchStudyMaterial,
  getTests,
  getTimetables,
  saveTimetable,
  deleteTimetable,
  getAllStudentDocuments,
  saveStudentDocument,
  deleteStudentDocument,
  getTestById,
  getBatchAnnouncements
} from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import toast from 'react-hot-toast';
import { useRealtime } from '../../contexts/RealtimeContext';
import { collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp, doc, updateDoc, deleteDoc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import BatchDetailsModalEnhanced from '../../components/coaching/BatchDetailsModalEnhanced';

const BatchManagement = () => {
  const { userProfile } = useRealtime();
  
  // Check if user is a super admin (not coaching admin)
  const isSuperAdmin = userProfile?.role === 'admin' || 
                       userProfile?.role === 'superadmin' || 
                       userProfile?.role === 'founder';
  const isCoachingAdmin = userProfile?.role === 'coaching_admin' || userProfile?.role === 'coaching_staff';
  
  // Only use coachingId if user is a coaching admin or staff, not a super admin
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);
  
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchClasses, setBatchClasses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [editingBatch, setEditingBatch] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [selectedClassForMaterial, setSelectedClassForMaterial] = useState(null);
  const [showBatchDetailsModal, setShowBatchDetailsModal] = useState(false);
  const [batchForModal, setBatchForModal] = useState(null);
  
  const [batchFormData, setBatchFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    instructorName: '',
    price: 0
  });

  const [classFormData, setClassFormData] = useState({
    subject: '',
    date: '',
    time: '',
    duration: 60,
    zoomLink: '',
    status: 'scheduled',
    isRevision: false,
    description: ''
  });

  const [enrollmentEmail, setEnrollmentEmail] = useState('');
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  
  // Announcements, Doubts, and Tests management
  const [batchAnnouncements, setBatchAnnouncements] = useState([]);
  const [batchDoubts, setBatchDoubts] = useState([]);
  const [batchTests, setBatchTests] = useState([]);
  const [batchTimetables, setBatchTimetables] = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [batchAdmitCards, setBatchAdmitCards] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showDoubtModal, setShowDoubtModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [answeringDoubt, setAnsweringDoubt] = useState(null);
  const [availableTests, setAvailableTests] = useState([]);
  
  const [announcementFormData, setAnnouncementFormData] = useState({
    title: '',
    content: ''
  });
  const [announcementFiles, setAnnouncementFiles] = useState([]);
  const [uploadingAnnouncementFiles, setUploadingAnnouncementFiles] = useState(false);
  
  const [doubtAnswer, setDoubtAnswer] = useState('');
  const [answerImages, setAnswerImages] = useState([]);
  const [uploadingAnswerImages, setUploadingAnswerImages] = useState(false);
  
  const [testFormData, setTestFormData] = useState({
    testId: '',
    testTitle: ''
  });

  const [timetableFormData, setTimetableFormData] = useState({
    title: '',
    description: '',
    pdfUrl: ''
  });
  const [uploadingTimetable, setUploadingTimetable] = useState(false);
  const [editingTimetable, setEditingTimetable] = useState(null);

  const [resultsFormData, setResultsFormData] = useState({
    studentEmail: '',
    studentId: '',
    studentName: '',
    examName: '',
    examDate: '',
    documentUrl: '',
    documentType: 'result'
  });
  const [uploadingResults, setUploadingResults] = useState(false);
  const [editingResult, setEditingResult] = useState(null);

  useEffect(() => {
    loadBatches();
  }, [coachingId]);

  // Set up real-time listeners for all batches
  useEffect(() => {
    if (batches.length === 0) return;
    
    const unsubscribers = [];
    
    // Set up listeners for each batch
    batches.forEach(batch => {
      loadBatchAnnouncements(batch.id); // async, no unsubscribe needed
      const unsubDoubts = loadBatchDoubts(batch.id);
      const unsubTests = loadBatchTests(batch.id);
      const unsubTimetables = loadBatchTimetables(batch.id);
      
      if (unsubDoubts) unsubscribers.push(unsubDoubts);
      if (unsubTests) unsubscribers.push(unsubTests);
      if (unsubTimetables) unsubscribers.push(unsubTimetables);
    });
    
    // Cleanup function
    return () => {
      unsubscribers.forEach(unsub => {
        if (unsub && typeof unsub === 'function') {
          unsub();
        }
      });
    };
  }, [batches]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const result = await getBatches(coachingId || null);
      if (result.success) {
        const batches = result.data || [];
        setBatches(batches);
      } else {
        toast.error(`Failed to load batches: ${result.error}`);
      }
    } catch (error) {
      toast.error(`Failed to load batches: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const loadBatchClasses = async (batchId) => {
    try {
      const result = await getBatchClasses(batchId);
      if (result.success) {
        setBatchClasses(result.data);
      }
    } catch (error) {
      toast.error('Failed to load classes');
    }
  };

  const loadEnrollments = async (batchId) => {
    try {
      const result = await getBatchEnrollments(batchId);
      if (result.success) {
        setEnrollments(result.data);
      }
    } catch (error) {
      toast.error('Failed to load enrollments');
    }
  };

  // Load announcements for batch (with URL conversion, merges with other batches)
  const loadBatchAnnouncements = async (batchId) => {
    if (!batchId) return;
    
    try {
      const result = await getBatchAnnouncements(batchId);
      if (result.success) {
        // Update state by merging with existing announcements from other batches
        setBatchAnnouncements(prev => {
          const filtered = prev.filter(a => a.batchId !== batchId);
          return [...filtered, ...result.data];
        });
      }
    } catch (error) {
      console.error('Error loading batch announcements:', error);
    }
  };

  // Load doubts for batch (real-time, merges with other batches)
  const loadBatchDoubts = (batchId) => {
    if (!batchId) return;
    
    const q = query(
      collection(db, 'batchDoubts'),
      where('batchId', '==', batchId),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const doubtsList = [];
      snapshot.forEach((doc) => {
        doubtsList.push({ id: doc.id, ...doc.data() });
      });
      // Update state by merging with existing doubts from other batches
      setBatchDoubts(prev => {
        const filtered = prev.filter(d => d.batchId !== batchId);
        return [...filtered, ...doubtsList];
      });
    }, (error) => {
      console.error('Error loading batch doubts:', error);
    });
    
    return unsubscribe;
  };

  // Load tests linked to batch (real-time, merges with other batches)
  const loadBatchTests = (batchId) => {
    if (!batchId) return;
    
    // Try with orderBy first
    let q = query(
      collection(db, 'batchTests'),
      where('batchId', '==', batchId),
      orderBy('addedAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const testsList = [];
      snapshot.forEach((doc) => {
        testsList.push({ id: doc.id, ...doc.data() });
      });
      // Sort client-side as well to ensure correct order
      testsList.sort((a, b) => {
        const aTime = a.addedAt?.toMillis?.() || new Date(a.addedAt || 0).getTime();
        const bTime = b.addedAt?.toMillis?.() || new Date(b.addedAt || 0).getTime();
        return bTime - aTime; // Descending
      });
      // Update state by merging with existing tests from other batches
      setBatchTests(prev => {
        const filtered = prev.filter(t => t.batchId !== batchId);
        return [...filtered, ...testsList];
      });
    }, (error) => {
      // If orderBy fails (missing index), try without it
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.warn('Index missing for batchTests query, using fallback');
        const fallbackQ = query(
          collection(db, 'batchTests'),
          where('batchId', '==', batchId)
        );
        const fallbackUnsubscribe = onSnapshot(fallbackQ, (snapshot) => {
          const testsList = [];
          snapshot.forEach((doc) => {
            testsList.push({ id: doc.id, ...doc.data() });
          });
          // Sort client-side
          testsList.sort((a, b) => {
            const aTime = a.addedAt?.toMillis?.() || new Date(a.addedAt || 0).getTime();
            const bTime = b.addedAt?.toMillis?.() || new Date(b.addedAt || 0).getTime();
            return bTime - aTime; // Descending
          });
          // Update state by merging with existing tests from other batches
          setBatchTests(prev => {
            const filtered = prev.filter(t => t.batchId !== batchId);
            return [...filtered, ...testsList];
          });
        }, (fallbackError) => {
          console.error('Error loading batch tests:', fallbackError);
        });
        return fallbackUnsubscribe;
      } else {
        console.error('Error loading batch tests:', error);
      }
    });
    
    return unsubscribe;
  };

  // Load available tests for the coaching
  const loadAvailableTests = async () => {
    try {
      const result = await getTests({ coachingId, status: 'published' });
      if (result.success) {
        setAvailableTests(result.data || []);
      }
    } catch (error) {
    }
  };

  // Load timetables for batch (real-time)
  const loadBatchTimetables = (batchId) => {
    if (!batchId) return;
    
    // Try with orderBy first
    let q = query(
      collection(db, 'timetables'),
      where('batchId', '==', batchId),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const timetablesList = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        timetablesList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || 0)
        });
      });
      // Update state by merging with existing timetables from other batches
      setBatchTimetables(prev => {
        const filtered = prev.filter(t => t.batchId !== batchId);
        return [...filtered, ...timetablesList];
      });
    }, (error) => {
      // If orderBy fails (missing index), try without it
      if (error.code === 'failed-precondition' || error.message?.includes('index')) {
        console.warn('Index missing for timetables query, using fallback');
        const fallbackQ = query(
          collection(db, 'timetables'),
          where('batchId', '==', batchId)
        );
        const fallbackUnsubscribe = onSnapshot(fallbackQ, (snapshot) => {
          const timetablesList = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            timetablesList.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || 0),
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || 0)
            });
          });
          // Sort client-side
          timetablesList.sort((a, b) => {
            const aTime = a.createdAt?.getTime ? a.createdAt.getTime() : (a.createdAt || 0);
            const bTime = b.createdAt?.getTime ? b.createdAt.getTime() : (b.createdAt || 0);
            return bTime - aTime; // Descending
          });
          // Update state by merging with existing timetables from other batches
          setBatchTimetables(prev => {
            const filtered = prev.filter(t => t.batchId !== batchId);
            return [...filtered, ...timetablesList];
          });
        }, (fallbackError) => {
          console.error('Error loading batch timetables:', fallbackError);
        });
        return fallbackUnsubscribe;
      } else {
        console.error('Error loading batch timetables:', error);
      }
    });
    
    return unsubscribe;
  };

  // Load results and admit cards for batch
  const loadBatchResults = async (batchId) => {
    if (!batchId) return;
    try {
      const [resultsResult, admitCardsResult] = await Promise.all([
        getAllStudentDocuments('result', { batchId }),
        getAllStudentDocuments('admitCard', { batchId })
      ]);
      if (resultsResult.success) {
        setBatchResults(resultsResult.data || []);
      }
      if (admitCardsResult.success) {
        setBatchAdmitCards(admitCardsResult.data || []);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  // Handle announcement file upload
  const handleAnnouncementFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf'
    ];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a valid file. Only images and PDFs are allowed.`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB`);
        continue;
      }
    }

    setUploadingAnnouncementFiles(true);
    const uploadedFiles = [];

    try {
      if (!selectedBatch) {
        toast.error('Please select a batch first');
        return;
      }

      for (const file of files) {
        const fileName = `announcement_${Date.now()}_${file.name}`;
        const storagePath = `batchAnnouncements/${selectedBatch.id}/${userProfile?.uid || userProfile?.id}/${fileName}`;
        const result = await uploadFile(file, storagePath);
        
        if (result.success) {
          uploadedFiles.push({
            url: result.url,
            name: file.name,
            type: file.type,
            size: file.size
          });
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      setAnnouncementFiles(prev => [...prev, ...uploadedFiles]);
      toast.success('Files uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploadingAnnouncementFiles(false);
    }
  };

  // Remove announcement file
  const handleRemoveAnnouncementFile = (index) => {
    setAnnouncementFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Announcement CRUD operations
  const handleCreateAnnouncement = async () => {
    if (!selectedBatch) return;
    if (!announcementFormData.title.trim() || !announcementFormData.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    try {
      await addDoc(collection(db, 'batchAnnouncements'), {
        batchId: selectedBatch.id,
        title: announcementFormData.title,
        content: announcementFormData.content,
        files: announcementFiles,
        createdBy: userProfile?.uid || userProfile?.id,
        createdByName: userProfile?.name || userProfile?.email || 'Admin',
        createdAt: serverTimestamp()
      });
      toast.success('Announcement created successfully');
      setAnnouncementFormData({ title: '', content: '' });
      setAnnouncementFiles([]);
      setShowAnnouncementModal(false);
      setEditingAnnouncement(null);
    } catch (error) {
      toast.error('Failed to create announcement');
    }
  };

  const handleUpdateAnnouncement = async (announcementId) => {
    if (!announcementFormData.title.trim() || !announcementFormData.content.trim()) {
      toast.error('Please fill in both title and content');
      return;
    }

    try {
      const announcementRef = doc(db, 'batchAnnouncements', announcementId);
      await updateDoc(announcementRef, {
        title: announcementFormData.title,
        content: announcementFormData.content,
        files: announcementFiles,
        updatedAt: serverTimestamp()
      });
      toast.success('Announcement updated successfully');
      setAnnouncementFormData({ title: '', content: '' });
      setAnnouncementFiles([]);
      setShowAnnouncementModal(false);
      setEditingAnnouncement(null);
    } catch (error) {
      toast.error('Failed to update announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await deleteDoc(doc(db, 'batchAnnouncements', announcementId));
      toast.success('Announcement deleted successfully');
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  // Handle answer image upload
  const handleAnswerImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name} is not a valid image file`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }
    }

    setUploadingAnswerImages(true);
    const uploadedImages = [];

    try {
      const currentDoubt = batchDoubts.find(d => d.id === answeringDoubt);
      const batchId = currentDoubt?.batchId;
      
      for (const file of files) {
        const fileName = `answer_${Date.now()}_${file.name}`;
        const storagePath = `batchDoubts/${batchId}/${answeringDoubt}/${fileName}`;
        const result = await uploadFile(file, storagePath);
        
        if (result.success) {
          uploadedImages.push(result.url);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      setAnswerImages(prev => [...prev, ...uploadedImages]);
      toast.success('Images uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingAnswerImages(false);
    }
  };

  // Remove answer image
  const handleRemoveAnswerImage = (index) => {
    setAnswerImages(prev => prev.filter((_, i) => i !== index));
  };

  // Doubt operations
  const handleAnswerDoubt = async (doubtId) => {
    if (!doubtAnswer.trim() && answerImages.length === 0) {
      toast.error('Please enter an answer or upload an image');
      return;
    }

    try {
      const doubtRef = doc(db, 'batchDoubts', doubtId);
      await updateDoc(doubtRef, {
        answer: doubtAnswer || '',
        answerImages: answerImages,
        answeredBy: userProfile?.uid || userProfile?.id,
        answeredByName: userProfile?.name || userProfile?.email || 'Admin',
        status: 'answered',
        answeredAt: serverTimestamp()
      });
      toast.success('Doubt answered successfully');
      setDoubtAnswer('');
      setAnswerImages([]);
      setAnsweringDoubt(null);
      setShowDoubtModal(false);
    } catch (error) {
      toast.error('Failed to answer doubt');
    }
  };

  const handleCloseDoubt = async (doubtId) => {
    try {
      const doubtRef = doc(db, 'batchDoubts', doubtId);
      await updateDoc(doubtRef, {
        status: 'closed',
        closedAt: serverTimestamp()
      });
      toast.success('Doubt closed successfully');
    } catch (error) {
      toast.error('Failed to close doubt');
    }
  };

  const handleDeleteDoubt = async (doubtId) => {
    if (!window.confirm('Are you sure you want to delete this doubt?')) return;

    try {
      await deleteDoc(doc(db, 'batchDoubts', doubtId));
      toast.success('Doubt deleted successfully');
    } catch (error) {
      toast.error('Failed to delete doubt');
    }
  };

  // Test operations
  const handleAddTestToBatch = async () => {
    if (!selectedBatch) return;
    if (!testFormData.testId.trim()) {
      toast.error('Please enter a test ID');
      return;
    }

    // Check if test already exists in batch
    const existingTest = batchTests.find(t => t.testId === testFormData.testId);
    if (existingTest) {
      toast.error('This test is already added to the batch');
      return;
    }

    // For coaching admins: Prevent adding admin tests (coachingId = null)
    if (coachingId && selectedBatch.coachingId) {
      try {
        const testResult = await getTestById(testFormData.testId);
        if (testResult.success && testResult.data) {
          const test = testResult.data;
          // If test is created by admin (coachingId = null), prevent adding it
          if (test.coachingId === null) {
            toast.error('You cannot add admin-created tests to your batch. Please use only tests created by your coaching.');
            return;
          }
          // If test belongs to a different coaching, prevent adding it
          if (test.coachingId !== selectedBatch.coachingId) {
            toast.error('This test belongs to a different coaching and cannot be added to your batch.');
            return;
          }
        }
      } catch (error) {
        toast.error('Failed to verify test. Please try again.');
        return;
      }
    }

    try {
      await addDoc(collection(db, 'batchTests'), {
        batchId: selectedBatch.id,
        testId: testFormData.testId,
        testTitle: testFormData.testTitle || testFormData.testId,
        addedBy: userProfile?.uid || userProfile?.id,
        addedByName: userProfile?.name || userProfile?.email || 'Admin',
        addedAt: serverTimestamp()
      });
      toast.success('Test added to batch successfully');
      setTestFormData({ testId: '', testTitle: '' });
      setShowTestModal(false);
    } catch (error) {
      toast.error('Failed to add test to batch');
    }
  };

  const handleRemoveTestFromBatch = async (batchTestId) => {
    if (!window.confirm('Are you sure you want to remove this test from the batch?')) return;

    try {
      await deleteDoc(doc(db, 'batchTests', batchTestId));
      toast.success('Test removed from batch successfully');
    } catch (error) {
      toast.error('Failed to remove test from batch');
    }
  };

  // Load data when batch is selected
  useEffect(() => {
    if (selectedBatch) {
      loadBatchClasses(selectedBatch.id);
      loadEnrollments(selectedBatch.id);
      loadBatchAnnouncements(selectedBatch.id); // async, no unsubscribe needed
      const unsubDoubts = loadBatchDoubts(selectedBatch.id);
      const unsubTests = loadBatchTests(selectedBatch.id);
      const unsubTimetables = loadBatchTimetables(selectedBatch.id);
      loadAvailableTests();
      
      return () => {
        if (unsubDoubts) unsubDoubts();
        if (unsubTests) unsubTests();
        if (unsubTimetables) unsubTimetables();
      };
    }
  }, [selectedBatch, coachingId]);

  const handleToggleBatch = async (batchId) => {
    const newExpanded = new Set(expandedBatches);
    if (newExpanded.has(batchId)) {
      newExpanded.delete(batchId);
    } else {
      newExpanded.add(batchId);
      await loadBatchClasses(batchId);
      await loadEnrollments(batchId);
      // Set up real-time listeners for this batch
      loadBatchAnnouncements(batchId);
      loadBatchDoubts(batchId);
      loadBatchTests(batchId);
      loadBatchTimetables(batchId);
      await loadBatchResults(batchId);
    }
    setExpandedBatches(newExpanded);
  };

  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!batchFormData.name.trim() || !batchFormData.startDate || !batchFormData.endDate) {
        toast.error('Name, start date, and end date are required');
        return;
      }

      // Determine coachingId value - must be string or null, never undefined
      const finalCoachingId = (coachingId && isCoachingAdmin) 
        ? coachingId 
        : null; // Super admins get null, not undefined
      
      const batchData = {
        ...batchFormData,
        startDate: new Date(batchFormData.startDate).toISOString(),
        endDate: new Date(batchFormData.endDate).toISOString(),
        price: parseFloat(batchFormData.price) || 0
      };
      
      // Only include coachingId if it has a valid value (not undefined)
      if (finalCoachingId !== undefined && finalCoachingId !== null) {
        batchData.coachingId = finalCoachingId;
      } else if (isSuperAdmin) {
        // Explicitly set to null for super admins to avoid undefined
        batchData.coachingId = null;
      }

      if (editingBatch) {
        const result = await updateBatch(editingBatch.id, batchData);
        if (result.success) {
          toast.success('Batch updated successfully');
          setShowBatchModal(false);
          setEditingBatch(null);
          loadBatches();
        } else {
          toast.error(`Failed to update batch: ${result.error || 'Unknown error'}`);
        }
      } else {
        const result = await createBatch(batchData);
        if (result.success) {
          toast.success('Batch created successfully');
          setShowBatchModal(false);
          // Reload batches after a short delay to ensure data is saved
          setTimeout(() => {
            loadBatches();
          }, 500);
        } else {
          toast.error(`Failed to create batch: ${result.error || 'Unknown error'}`);
        }
      }
      setBatchFormData({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        instructorName: '',
        price: 0
      });
    } catch (error) {
      toast.error('Failed to save batch');
    }
  };

  const handleClassSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!classFormData.subject.trim() || !classFormData.date || !classFormData.time) {
        toast.error('Subject, date, and time are required');
        return;
      }

      const classData = {
        ...classFormData,
        batchId: selectedBatch.id,
        date: new Date(classFormData.date).toISOString().split('T')[0],
        duration: parseInt(classFormData.duration) || 60
      };

      if (editingClass) {
        const result = await updateBatchClass(editingClass.id, classData);
        if (result.success) {
          toast.success('Class updated successfully');
          setShowClassModal(false);
          setEditingClass(null);
          await loadBatchClasses(selectedBatch.id);
        } else {
          toast.error('Failed to update class');
        }
      } else {
        const result = await addBatchClass(classData);
        if (result.success) {
          toast.success('Class added successfully');
          setShowClassModal(false);
          await loadBatchClasses(selectedBatch.id);
        } else {
          toast.error('Failed to add class');
        }
      }
      setClassFormData({
        subject: '',
        date: '',
        time: '',
        duration: 60,
        zoomLink: '',
        status: 'scheduled',
        isRevision: false,
        description: ''
      });
    } catch (error) {
      toast.error('Failed to save class');
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (window.confirm('Are you sure you want to delete this batch?')) {
      try {
        // Pass coachingId to the delete function to trigger validation
        const result = await deleteBatch(batchId, coachingId);
        if (result.success) {
          toast.success('Batch deleted successfully');
          loadBatches();
        } else {
          // If deletion failed due to students, show a specific message
          if (result.studentCount > 0) {
            toast.error(`Cannot delete batch: ${result.studentCount} students are still enrolled. Please remove them first.`);
          } else {
            toast.error(result.error || 'Failed to delete batch');
          }
        }
      } catch (error) {
        console.error('Delete batch error:', error);
        toast.error('Failed to delete batch');
      }
    }
  };

  const handleDeleteClass = async (classId) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        const result = await deleteBatchClass(classId);
        if (result.success) {
          toast.success('Class deleted successfully');
          await loadBatchClasses(selectedBatch.id);
        } else {
          toast.error('Failed to delete class');
        }
      } catch (error) {
        toast.error('Failed to delete class');
      }
    }
  };

  const handleEnrollStudent = async () => {
    if (!enrollmentEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      const result = await enrollStudentInBatch(selectedBatch.id, enrollmentEmail);
      if (result.success) {
        toast.success('Student enrolled successfully');
        setEnrollmentEmail('');
        await loadEnrollments(selectedBatch.id);
      } else {
        toast.error(result.error || 'Failed to enroll student');
      }
    } catch (error) {
      toast.error('Failed to enroll student');
    }
  };

  const handleRemoveEnrollment = async (enrollmentId, userId) => {
    if (window.confirm('Are you sure you want to remove this student?')) {
      try {
        const result = await removeBatchEnrollment(selectedBatch.id, userId);
        if (result.success) {
          toast.success('Student removed successfully');
          await loadEnrollments(selectedBatch.id);
        } else {
          toast.error('Failed to remove student');
        }
      } catch (error) {
        toast.error('Failed to remove student');
      }
    }
  };

  const handleRecordingUpload = async (e, classId) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file');
      return;
    }

    try {
      setUploadingRecording(true);
      const fileName = `batch-recordings/${classId}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      
      if (result.success) {
        await updateBatchClass(classId, { recordingUrl: result.url, status: 'completed' });
        toast.success('Recording uploaded successfully');
        await loadBatchClasses(selectedBatch.id);
      } else {
        toast.error('Failed to upload recording');
      }
    } catch (error) {
      toast.error('Failed to upload recording');
    } finally {
      setUploadingRecording(false);
    }
  };

  const handleMaterialUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingMaterial(true);
      const fileName = `batch-materials/${selectedClassForMaterial.id}/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      
      if (result.success) {
        await addBatchStudyMaterial(selectedClassForMaterial.id, {
          title: file.name,
          url: result.url,
          type: file.type,
          size: file.size
        });
        toast.success('Study material uploaded successfully');
        setShowMaterialModal(false);
        setSelectedClassForMaterial(null);
      } else {
        toast.error('Failed to upload material');
      }
    } catch (error) {
      toast.error('Failed to upload material');
    } finally {
      setUploadingMaterial(false);
    }
  };

  const handleEditBatch = (batch) => {
    setEditingBatch(batch);
    try {
      let startDateStr = '';
      let endDateStr = '';
      
      if (batch.startDate) {
        const startDate = batch.startDate instanceof Date 
          ? batch.startDate 
          : new Date(batch.startDate);
        startDateStr = startDate.toISOString().split('T')[0];
      }
      
      if (batch.endDate) {
        const endDate = batch.endDate instanceof Date 
          ? batch.endDate 
          : new Date(batch.endDate);
        endDateStr = endDate.toISOString().split('T')[0];
      }
      
      setBatchFormData({
        name: batch.name || '',
        description: batch.description || '',
        startDate: startDateStr,
        endDate: endDateStr,
        instructorName: batch.instructorName || '',
        price: batch.price || 0
      });
      setShowBatchModal(true);
    } catch (error) {
      toast.error('Failed to load batch data');
    }
  };

  const handleEditClass = (classItem) => {
    setEditingClass(classItem);
    try {
      let dateStr = '';
      if (classItem.date) {
        // Handle Firestore Timestamp or Date string
        let dateObj;
        if (classItem.date?.toDate) {
          dateObj = classItem.date.toDate();
        } else if (classItem.date instanceof Date) {
          dateObj = classItem.date;
        } else {
          dateObj = new Date(classItem.date);
        }
        dateStr = dateObj.toISOString().split('T')[0];
      }
      
      setClassFormData({
        subject: classItem.subject || '',
        date: dateStr,
        time: classItem.time || '',
        duration: classItem.duration || 60,
        zoomLink: classItem.zoomLink || '',
        status: classItem.status || 'scheduled',
        isRevision: classItem.isRevision || false,
        description: classItem.description || ''
      });
      setShowClassModal(true);
    } catch (error) {
      toast.error('Failed to load class data');
    }
  };

  const filteredBatches = batches.filter(batch => 
    batch.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    batch.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (showBatchModal) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {editingBatch ? 'Edit Batch' : 'Create New Batch'}
          </h1>
          <button
            onClick={() => {
              setShowBatchModal(false);
              setEditingBatch(null);
              setBatchFormData({
                name: '',
                description: '',
                startDate: '',
                endDate: '',
                instructorName: ''
              });
            }}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleBatchSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Batch Name *</label>
              <input
                type="text"
                value={batchFormData.name}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
              <textarea
                value={batchFormData.description}
                onChange={(e) => setBatchFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Date *</label>
                <input
                  type="date"
                  value={batchFormData.startDate}
                  onChange={(e) => setBatchFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">End Date *</label>
                <input
                  type="date"
                  value={batchFormData.endDate}
                  onChange={(e) => setBatchFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Instructor Name</label>
                <input
                  type="text"
                  value={batchFormData.instructorName}
                  onChange={(e) => setBatchFormData(prev => ({ ...prev, instructorName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Batch Fee (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={batchFormData.price}
                  onChange={(e) => setBatchFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Enter 0 for free enrollment
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowBatchModal(false);
                  setEditingBatch(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                <Save className="w-4 h-4 inline mr-2" />
                {editingBatch ? 'Update' : 'Create'} Batch
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  if (showClassModal && selectedBatch) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {editingClass ? 'Edit Class' : 'Add New Class'} - {selectedBatch.name}
          </h1>
          <button
            onClick={() => {
              setShowClassModal(false);
              setEditingClass(null);
            }}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleClassSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Subject *</label>
                <input
                  type="text"
                  value={classFormData.subject}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date *</label>
                <input
                  type="date"
                  value={classFormData.date}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time *</label>
                <input
                  type="time"
                  value={classFormData.time}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Duration (minutes)</label>
                <input
                  type="number"
                  value={classFormData.duration}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 60 }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  min="15"
                  step="15"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Zoom Link</label>
                <input
                  type="url"
                  value={classFormData.zoomLink}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, zoomLink: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="https://zoom.us/j/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                <select
                  value={classFormData.status}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="scheduled">Scheduled</option>
                  <option value="live">Live</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={classFormData.isRevision}
                  onChange={(e) => setClassFormData(prev => ({ ...prev, isRevision: e.target.checked }))}
                  className="rounded border-gray-300"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Revision Class</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
              <textarea
                value={classFormData.description}
                onChange={(e) => setClassFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowClassModal(false);
                  setEditingClass(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                <Save className="w-4 h-4 inline mr-2" />
                {editingClass ? 'Update' : 'Add'} Class
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  if (showEnrollmentModal && selectedBatch) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Enroll Students - {selectedBatch.name}
          </h1>
          <button
            onClick={() => setShowEnrollmentModal(false)}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Student Email Address
            </label>
            <div className="flex space-x-2">
              <input
                type="email"
                value={enrollmentEmail}
                onChange={(e) => setEnrollmentEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleEnrollStudent()}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="student@example.com"
              />
              <button
                onClick={handleEnrollStudent}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Enroll
              </button>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Enrolled Students ({enrollments.length})</h3>
            <div className="space-y-2">
              {enrollments.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-4">No students enrolled yet</p>
              ) : (
                enrollments.map((enrollment) => (
                  <div
                    key={enrollment.id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {enrollment.userData?.name || enrollment.userData?.displayName || 'Unknown User'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{enrollment.email}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveEnrollment(enrollment.id, enrollment.userId)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showMaterialModal && selectedClassForMaterial) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Study Materials - {selectedClassForMaterial.subject}
          </h1>
          <button
            onClick={() => {
              setShowMaterialModal(false);
              setSelectedClassForMaterial(null);
            }}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload Study Material
            </label>
            <input
              type="file"
              onChange={handleMaterialUpload}
              disabled={uploadingMaterial}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
            />
            {uploadingMaterial && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Uploading...</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Batch Management</h1>
        <button
          onClick={() => {
            setEditingBatch(null);
            setBatchFormData({
              name: '',
              description: '',
              startDate: '',
              endDate: '',
              instructorName: ''
            });
            setShowBatchModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
        >
          <Plus className="w-5 h-5" />
          <span>Create Batch</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex items-center space-x-2">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search batches..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredBatches.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No batches found. Create your first batch!
            </div>
          ) : (
            filteredBatches.map((batch) => {
              const isExpanded = expandedBatches.has(batch.id);
              const batchClassesFiltered = isExpanded ? batchClasses.filter(c => c.batchId === batch.id) : [];
              const enrollmentsFiltered = isExpanded ? enrollments.filter(e => e.batchId === batch.id) : [];

              return (
                <div
                  key={batch.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700"
                >
                  <div
                    onClick={() => {
                      setBatchForModal(batch);
                      setShowBatchDetailsModal(true);
                    }}
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{batch.name}</h3>
                          {batch.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">{batch.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBatch(batch);
                            setShowEnrollmentModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                          title="Manage Enrollments"
                        >
                          <Users className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditBatch(batch);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                          title="Edit Batch"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBatch(batch.id);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          title="Delete Batch"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Inline expansion removed - now opens in modal */}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingAnnouncement ? 'Edit Announcement' : 'Add Announcement'}
              </h3>
              <button
                onClick={() => {
                  setShowAnnouncementModal(false);
                  setEditingAnnouncement(null);
                  setAnnouncementFormData({ title: '', content: '' });
                  setAnnouncementFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={announcementFormData.title}
                  onChange={(e) => setAnnouncementFormData({ ...announcementFormData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter announcement title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Content
                </label>
                <textarea
                  value={announcementFormData.content}
                  onChange={(e) => setAnnouncementFormData({ ...announcementFormData, content: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter announcement content"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload Files (Images or PDFs) - Optional
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleAnnouncementFileUpload}
                    disabled={uploadingAnnouncementFiles}
                    className="hidden"
                    id="announcement-file-upload"
                  />
                  <label
                    htmlFor="announcement-file-upload"
                    className={`flex flex-col items-center justify-center cursor-pointer ${uploadingAnnouncementFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {uploadingAnnouncementFiles ? 'Uploading...' : 'Click to upload images or PDFs (Max 10MB each)'}
                    </span>
                  </label>
                </div>
                {announcementFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {announcementFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-2 flex-1">
                          {file.type && file.type.startsWith('image/') ? (
                            <ImageIcon className="w-5 h-5 text-blue-600" />
                          ) : (
                            <FileText className="w-5 h-5 text-red-600" />
                          )}
                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveAnnouncementFile(index)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowAnnouncementModal(false);
                    setEditingAnnouncement(null);
                    setAnnouncementFormData({ title: '', content: '' });
                    setAnnouncementFiles([]);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={editingAnnouncement ? () => handleUpdateAnnouncement(editingAnnouncement.id) : handleCreateAnnouncement}
                  disabled={uploadingAnnouncementFiles}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingAnnouncement ? 'Update' : 'Create'} Announcement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Doubt Answer Modal */}
      {showDoubtModal && answeringDoubt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Answer Doubt</h3>
              <button
                onClick={() => {
                  setShowDoubtModal(false);
                  setAnsweringDoubt(null);
                  setDoubtAnswer('');
                  setAnswerImages([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              {batchDoubts.find(d => d.id === answeringDoubt) && (() => {
                const currentDoubt = batchDoubts.find(d => d.id === answeringDoubt);
                return (
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg mb-4">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Question:</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {currentDoubt.question}
                    </p>
                    {currentDoubt.questionImages && currentDoubt.questionImages.length > 0 && (
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        {currentDoubt.questionImages.map((imageUrl, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={imageUrl}
                              alt={`Question image ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer"
                              onClick={() => window.open(imageUrl, '_blank')}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your Answer
                </label>
                <textarea
                  value={doubtAnswer}
                  onChange={(e) => setDoubtAnswer(e.target.value)}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter your answer..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Upload Images (Optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleAnswerImageUpload}
                    disabled={uploadingAnswerImages}
                    className="hidden"
                    id="answer-image-upload"
                  />
                  <label
                    htmlFor="answer-image-upload"
                    className={`flex flex-col items-center justify-center cursor-pointer ${uploadingAnswerImages ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {uploadingAnswerImages ? 'Uploading...' : 'Click to upload images (Max 5MB each)'}
                    </span>
                  </label>
                </div>
                {answerImages.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {answerImages.map((imageUrl, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageUrl}
                          alt={`Answer image ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                        />
                        <button
                          onClick={() => handleRemoveAnswerImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDoubtModal(false);
                    setAnsweringDoubt(null);
                    setDoubtAnswer('');
                    setAnswerImages([]);
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAnswerDoubt(answeringDoubt)}
                  disabled={uploadingAnswerImages}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit Answer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Test Modal */}
      {showTestModal && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add Test to Batch</h3>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestFormData({ testId: '', testTitle: '' });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Test ID *
                </label>
                <input
                  type="text"
                  value={testFormData.testId}
                  onChange={(e) => setTestFormData({ ...testFormData, testId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter test ID (e.g., test-123)"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This ID will be used to redirect students to the test in mock tests section
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Test Title (Optional)
                </label>
                <input
                  type="text"
                  value={testFormData.testTitle}
                  onChange={(e) => setTestFormData({ ...testFormData, testTitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter test title (will use Test ID if empty)"
                />
              </div>
              
              {/* Show available tests */}
              {availableTests.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Or Select from Available Tests
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md">
                    {availableTests.map((test) => (
                      <div
                        key={test.id}
                        onClick={() => {
                          setTestFormData({
                            testId: test.id,
                            testTitle: test.title || test.id
                          });
                        }}
                        className={`p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          testFormData.testId === test.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                        }`}
                      >
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{test.title || test.id}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">ID: {test.id}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTestModal(false);
                    setTestFormData({ testId: '', testTitle: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTestToBatch}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md"
                >
                  Add Test
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timetable Modal */}
      {showTimetableModal && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingTimetable ? 'Edit Timetable' : 'Add Timetable'}
              </h3>
              <button
                onClick={() => {
                  setShowTimetableModal(false);
                  setEditingTimetable(null);
                  setTimetableFormData({ title: '', description: '', pdfUrl: '' });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={timetableFormData.title}
                  onChange={(e) => setTimetableFormData({ ...timetableFormData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Weekly Timetable"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={timetableFormData.description}
                  onChange={(e) => setTimetableFormData({ ...timetableFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  PDF File *
                </label>
                {timetableFormData.pdfUrl ? (
                  <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">PDF Uploaded</p>
                      <a
                        href={timetableFormData.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        View PDF
                      </a>
                    </div>
                    <button
                      onClick={() => setTimetableFormData({ ...timetableFormData, pdfUrl: '' })}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    {uploadingTimetable ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        <span>Upload PDF (Max 10MB)</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={async (e) => {
                        if (e.target.files[0]) {
                          const file = e.target.files[0];
                          if (file.type !== 'application/pdf') {
                            toast.error('Please upload a PDF file');
                            return;
                          }
                          try {
                            setUploadingTimetable(true);
                            const fileName = `timetables/${selectedBatch.id}/${Date.now()}_${file.name}`;
                            const result = await uploadFile(file, fileName);
                            if (result.success) {
                              setTimetableFormData({ ...timetableFormData, pdfUrl: result.url });
                              toast.success('PDF uploaded successfully');
                            } else {
                              toast.error('Failed to upload PDF');
                            }
                          } catch (error) {
                            toast.error('Error uploading file');
                          } finally {
                            setUploadingTimetable(false);
                          }
                        }
                      }}
                      disabled={uploadingTimetable}
                    />
                  </label>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowTimetableModal(false);
                    setEditingTimetable(null);
                    setTimetableFormData({ title: '', description: '', pdfUrl: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!timetableFormData.title.trim()) {
                      toast.error('Please enter timetable title');
                      return;
                    }
                    if (!timetableFormData.pdfUrl) {
                      toast.error('Please upload a PDF file');
                      return;
                    }
                    try {
                      const result = editingTimetable
                        ? await saveTimetable({ ...timetableFormData, id: editingTimetable.id, batchId: selectedBatch.id, coachingId })
                        : await saveTimetable({ ...timetableFormData, batchId: selectedBatch.id, coachingId, isGlobal: false });
                      if (result.success) {
                        toast.success(editingTimetable ? 'Timetable updated' : 'Timetable added');
                        setShowTimetableModal(false);
                        setEditingTimetable(null);
                        setTimetableFormData({ title: '', description: '', pdfUrl: '' });
                        // Real-time listener will update automatically
                      } else {
                        toast.error('Failed to save timetable');
                      }
                    } catch (error) {
                      toast.error('Error saving timetable');
                    }
                  }}
                  disabled={uploadingTimetable}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
                >
                  {editingTimetable ? 'Update' : 'Add'} Timetable
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results & Admit Cards Modal */}
      {showResultsModal && selectedBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingResult ? 'Edit Document' : 'Add Result/Admit Card'}
              </h3>
              <button
                onClick={() => {
                  setShowResultsModal(false);
                  setEditingResult(null);
                  setResultsFormData({
                    studentEmail: '',
                    studentId: '',
                    studentName: '',
                    examName: '',
                    examDate: '',
                    documentUrl: '',
                    documentType: 'result'
                  });
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Document Type
                </label>
                <select
                  value={resultsFormData.documentType}
                  onChange={(e) => setResultsFormData({ ...resultsFormData, documentType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="result">Result/Marksheet</option>
                  <option value="admitCard">Admit Card</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student Email *
                </label>
                  <input
                    type="email"
                    value={resultsFormData.studentEmail}
                    onChange={(e) => setResultsFormData({ ...resultsFormData, studentEmail: e.target.value.toLowerCase().trim() })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="student@example.com"
                    required
                  />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student Name
                </label>
                <input
                  type="text"
                  value={resultsFormData.studentName}
                  onChange={(e) => setResultsFormData({ ...resultsFormData, studentName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Student full name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Exam Name
                  </label>
                  <input
                    type="text"
                    value={resultsFormData.examName}
                    onChange={(e) => setResultsFormData({ ...resultsFormData, examName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., JEE Main 2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Exam Date
                  </label>
                  <input
                    type="date"
                    value={resultsFormData.examDate}
                    onChange={(e) => setResultsFormData({ ...resultsFormData, examDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Document File * (PDF or Image)
                </label>
                {resultsFormData.documentUrl ? (
                  <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">File Uploaded</p>
                      <a
                        href={resultsFormData.documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        View Document
                      </a>
                    </div>
                    <button
                      onClick={() => setResultsFormData({ ...resultsFormData, documentUrl: '' })}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    {uploadingResults ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        <span>Upload PDF or Image (Max 10MB)</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      className="hidden"
                      onChange={async (e) => {
                        if (e.target.files[0]) {
                          const file = e.target.files[0];
                          const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
                          if (!allowedTypes.includes(file.type)) {
                            toast.error('Please upload a PDF or image file');
                            return;
                          }
                          try {
                            setUploadingResults(true);
                            const fileName = `${resultsFormData.documentType}s/${selectedBatch.id}/${Date.now()}_${file.name}`;
                            const result = await uploadFile(file, fileName);
                            if (result.success) {
                              setResultsFormData({ ...resultsFormData, documentUrl: result.url });
                              toast.success('File uploaded successfully');
                            } else {
                              toast.error('Failed to upload file');
                            }
                          } catch (error) {
                            toast.error('Error uploading file');
                          } finally {
                            setUploadingResults(false);
                          }
                        }
                      }}
                      disabled={uploadingResults}
                    />
                  </label>
                )}
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowResultsModal(false);
                    setEditingResult(null);
                    setResultsFormData({
                      studentEmail: '',
                      studentId: '',
                      studentName: '',
                      examName: '',
                      examDate: '',
                      documentUrl: '',
                      documentType: 'result'
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!resultsFormData.studentEmail.trim()) {
                      toast.error('Please enter student email');
                      return;
                    }
                    if (!resultsFormData.documentUrl) {
                      toast.error('Please upload a document');
                      return;
                    }
                    try {
                      const result = editingResult
                        ? await saveStudentDocument({ ...resultsFormData, id: editingResult.id, batchId: selectedBatch.id, coachingId })
                        : await saveStudentDocument({ ...resultsFormData, batchId: selectedBatch.id, coachingId });
                      if (result.success) {
                        toast.success(editingResult ? 'Document updated' : 'Document added');
                        setShowResultsModal(false);
                        setEditingResult(null);
                        setResultsFormData({
                          studentEmail: '',
                          studentId: '',
                          studentName: '',
                          examName: '',
                          examDate: '',
                          documentUrl: '',
                          documentType: 'result'
                        });
                        await loadBatchResults(selectedBatch.id);
                      } else {
                        toast.error('Failed to save document');
                      }
                    } catch (error) {
                      toast.error('Error saving document');
                    }
                  }}
                  disabled={uploadingResults}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50"
                >
                  {editingResult ? 'Update' : 'Add'} Document
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch Details Modal */}
      {showBatchDetailsModal && batchForModal && (
        <BatchDetailsModalEnhanced
          batch={batchForModal}
          coachingId={coachingId}
          onClose={() => {
            setShowBatchDetailsModal(false);
            setBatchForModal(null);
          }}
          onEdit={() => {
            handleEditBatch(batchForModal);
            setShowBatchDetailsModal(false);
          }}
          onRefresh={() => {
            loadBatches();
          }}
        />
      )}
    </div>
  );
};

export default BatchManagement;

